import React, { useEffect, useState } from "react";
import { X, Trash, Clock, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  listHistory,
  removeHistory,
  clearHistory,
  formatRelative,
} from "../../lib/historyLog";
import { formatMoney } from "../../lib/currency";

/**
 * Shared bottom-sheet modal that surfaces the last activity from the
 * localStorage activity log. Used by Translate (kind="translate") and
 * Currency (kind="currency") panels via their Clock/History button.
 *
 * Props:
 *   - kind: "translate" | "currency"
 *   - open: boolean
 *   - onClose: () => void
 *   - onReuse?: (entry) => void  // called when user taps an entry
 */
export default function HistorySheet({ kind, open, onClose, onReuse }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (open) setItems(listHistory(kind));
  }, [open, kind]);

  if (!open) return null;

  const title = kind === "translate" ? "Recent translations" : "Recent conversions";
  const emptyMsg =
    kind === "translate"
      ? "Translate a phrase and it'll show up here."
      : "Convert a currency and it'll show up here.";

  function onRemove(id) {
    removeHistory(kind, id);
    setItems(listHistory(kind));
  }

  function onClear() {
    if (!items.length) return;
    clearHistory(kind);
    setItems([]);
    toast.message("History cleared");
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-start justify-center p-3 pt-6"
      data-testid={`history-sheet-${kind}`}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-white rounded-3xl shadow-deep max-h-[88vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 pt-4 pb-3 border-b border-navy/5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock size={22} weight="duotone" color="#0E2C54" />
            <h3 className="font-jakarta font-extrabold text-navy text-lg">
              {title}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            {items.length > 0 && (
              <button
                type="button"
                onClick={onClear}
                data-testid="history-clear-btn"
                className="text-navy-soft font-jakarta font-bold text-xs uppercase tracking-wider"
              >
                Clear
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              data-testid="history-close-btn"
              aria-label="Close"
              className="w-9 h-9 rounded-full bg-bone/80 border border-navy/10 flex items-center justify-center"
            >
              <X size={18} weight="bold" color="#0E2C54" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {items.length === 0 ? (
            <div className="text-center py-10">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-bone/80 flex items-center justify-center mb-3">
                <Clock size={28} weight="duotone" color="#4A5D78" />
              </div>
              <div className="font-jakarta font-extrabold text-navy text-base">
                Nothing yet
              </div>
              <div className="text-navy-soft text-sm font-jakarta font-medium mt-1">
                {emptyMsg}
              </div>
            </div>
          ) : (
            <ul className="space-y-2">
              {items.map((it) => (
                <li key={it.id} className="bg-bone/50 rounded-2xl px-3 py-3 flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    {kind === "translate" ? (
                      <TranslateRow entry={it} />
                    ) : (
                      <CurrencyRow entry={it} />
                    )}
                    <div className="text-navy-soft text-[11px] font-jakarta font-bold uppercase tracking-wider mt-1">
                      {formatRelative(it.at)}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    {onReuse && (
                      <button
                        type="button"
                        onClick={() => {
                          onReuse(it);
                          onClose?.();
                        }}
                        data-testid="history-reuse-btn"
                        aria-label="Reuse"
                        className="text-orange font-jakarta font-extrabold text-xs uppercase tracking-wider"
                      >
                        Reuse
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => onRemove(it.id)}
                      aria-label="Remove"
                      className="w-8 h-8 rounded-full hover:bg-white flex items-center justify-center"
                    >
                      <Trash size={16} weight="duotone" color="#9CA3AF" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function TranslateRow({ entry }) {
  return (
    <>
      <div className="text-navy font-jakarta font-semibold text-sm leading-snug truncate">
        {entry.source}
      </div>
      <div className="text-forest font-jakarta font-semibold text-sm leading-snug truncate">
        {entry.translation}
      </div>
      <div className="text-navy-soft text-[11px] font-jakarta font-bold mt-0.5 flex items-center gap-1">
        {entry.src}
        <ArrowRight size={10} weight="bold" />
        {entry.tgt}
      </div>
    </>
  );
}

function CurrencyRow({ entry }) {
  const fromText = formatMoneySafe(entry.amount, entry.from);
  const toText = formatMoneySafe(entry.result, entry.to);
  return (
    <>
      <div className="font-jakarta font-extrabold text-navy text-base flex items-center gap-2">
        <span>{fromText}</span>
        <ArrowRight size={14} weight="bold" color="#4A5D78" />
        <span>{toText}</span>
      </div>
      {Number.isFinite(entry.rate) && (
        <div className="text-navy-soft text-xs font-jakarta font-medium mt-0.5">
          1 {entry.from} = {Number(entry.rate).toFixed(4)} {entry.to}
        </div>
      )}
    </>
  );
}

function formatMoneySafe(amount, code) {
  const n = Number(amount);
  if (!Number.isFinite(n) || !code) return `${amount ?? "—"} ${code ?? ""}`.trim();
  try {
    return formatMoney(n, code);
  } catch {
    return `${n.toLocaleString()} ${code}`;
  }
}
